/**
 * Slash-command menu shown near text caret while editing.
 */

import { openPopoverMenu } from '../ui/popover-menu.js';
import { escapeHTML as escapeHtml } from '../shared/html-escape.js';

let menuEl = null;
let currentAnchorRect = null;
let onSelectCb = null;
let onCloseCb = null;
let restoreFocusCb = null;
let _handle = null;
let items = [];
let activeIndex = -1;
let filterQuery = '';

function normalizeAnchorRect(anchorRect) {
  if (!anchorRect) return null;
  const left = Number(anchorRect.left || 0);
  const top = Number(anchorRect.top || 0);
  const width = Number(anchorRect.width || 0);
  const height = Number(anchorRect.height || 0);
  return {
    left,
    top,
    width,
    height,
    right: Number(anchorRect.right ?? left + width),
    bottom: Number(anchorRect.bottom ?? top + height)
  };
}

function getEnabledIndexes() {
  const out = [];
  for (let i = 0; i < items.length; i += 1) {
    if (!items[i]?.disabled && matchesFilter(items[i])) out.push(i);
  }
  return out;
}

function matchesFilter(entry) {
  if (!filterQuery) return true;
  const haystack =
    `${entry?.id || ''} ${entry?.label || ''} ${entry?.description || ''}`.toLowerCase();
  return haystack.includes(filterQuery);
}

function ensureActiveIndex() {
  const enabled = getEnabledIndexes();
  if (!enabled.length) {
    activeIndex = -1;
    return;
  }
  if (!enabled.includes(activeIndex)) {
    activeIndex = enabled[0];
  }
}

function paintActive() {
  if (!menuEl) return;
  for (const row of menuEl.querySelectorAll('.pb-slash-menu-item')) {
    const rowIndex = Number(row.dataset.index || -1);
    const isVisible = matchesFilter(items[rowIndex]);
    const isActive = rowIndex === activeIndex;
    row.hidden = !isVisible;
    row.classList.toggle('pb-slash-menu-item-active', isActive);
    row.setAttribute('aria-selected', isActive ? 'true' : 'false');
    row.tabIndex = isActive ? 0 : -1;
  }
  const empty = menuEl.querySelector('.pb-slash-menu-empty');
  if (empty) empty.hidden = getEnabledIndexes().length > 0;
}

function selectIndex(index, close) {
  const entry = items[index];
  if (!entry || entry.disabled) return;
  if (typeof onSelectCb === 'function') onSelectCb(entry);
  close();
}

function buildSlashMenuFragment({ close }) {
  const fragment = document.createDocumentFragment();
  const root = document.createElement('div');
  root.className = 'pb-slash-menu-content';
  root.innerHTML = `
    <div class="pb-slash-menu-head">Slash Commands</div>
    <input class="pb-slash-menu-filter" type="search" aria-label="Filter slash commands" placeholder="Filter commands" autocomplete="off">
    <div class="pb-slash-menu-list">
      ${items
        .map(
          (entry) => `
        <button
          type="button"
          class="pb-slash-menu-item${entry.disabled ? ' pb-slash-menu-item-disabled' : ''}"
          role="menuitem"
          data-index="${entry.index}"
          data-id="${escapeHtml(entry.id)}"
          ${entry.disabled ? 'disabled' : ''}
        >
          <span class="pb-slash-menu-item-label">${escapeHtml(entry.label)}</span>
          ${entry.description ? `<span class="pb-slash-menu-item-sub">${escapeHtml(entry.description)}</span>` : ''}
        </button>
      `
        )
        .join('')}
      <div class="pb-slash-menu-empty" role="status" hidden>No matching commands</div>
    </div>
  `;

  const filterInput = root.querySelector('.pb-slash-menu-filter');
  filterInput?.addEventListener('input', () => {
    filterQuery = String(filterInput.value || '')
      .trim()
      .toLowerCase();
    ensureActiveIndex();
    paintActive();
  });

  root.addEventListener('click', (event) => {
    const item = event.target.closest('.pb-slash-menu-item[data-index]');
    if (!item) return;
    const index = Number(item.dataset.index || -1);
    if (!Number.isFinite(index) || index < 0) return;
    selectIndex(index, close);
  });

  root.addEventListener('mousemove', (event) => {
    const item = event.target.closest('.pb-slash-menu-item[data-index]');
    if (!item) return;
    const index = Number(item.dataset.index || -1);
    if (!Number.isFinite(index) || index < 0) return;
    if (items[index]?.disabled) return;
    if (activeIndex !== index) {
      activeIndex = index;
      paintActive();
    }
  });

  root.addEventListener('focusin', (event) => {
    const item = event.target.closest?.('.pb-slash-menu-item[data-index]');
    if (!item) return;
    const index = Number(item.dataset.index || -1);
    if (!Number.isFinite(index) || index < 0 || items[index]?.disabled) return;
    if (activeIndex !== index) {
      activeIndex = index;
      paintActive();
    }
  });

  root.addEventListener('keydown', (event) => {
    const enabled = getEnabledIndexes();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!enabled.length) return;
      const current = Math.max(0, enabled.indexOf(activeIndex));
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      activeIndex = enabled[(current + delta + enabled.length) % enabled.length];
      paintActive();
      menuEl
        ?.querySelector(`.pb-slash-menu-item[data-index="${activeIndex}"]`)
        ?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (event.key === 'Enter' && event.target === filterInput && activeIndex >= 0) {
      event.preventDefault();
      selectIndex(activeIndex, close);
    }
  });

  fragment.appendChild(root);
  return fragment;
}

function clearStateAfterClose() {
  const cb = onCloseCb;
  menuEl = null;
  currentAnchorRect = null;
  items = [];
  activeIndex = -1;
  filterQuery = '';
  onCloseCb = null;
  onSelectCb = null;
  restoreFocusCb = null;
  _handle = null;
  if (typeof cb === 'function') cb();
}

export function closeSlashMenu() {
  if (_handle) {
    _handle();
    return;
  }
  clearStateAfterClose();
}

export function isSlashMenuOpen() {
  return !!menuEl;
}

export function positionSlashMenu(anchorRect) {
  if (anchorRect) currentAnchorRect = normalizeAnchorRect(anchorRect);
  _handle?.reposition?.();
}

export function openSlashMenu({
  anchorRect,
  commands = [],
  onSelect = null,
  onClose = null,
  restoreFocus = null
}) {
  closeSlashMenu();
  if (!Array.isArray(commands) || !commands.length) return;

  currentAnchorRect = normalizeAnchorRect(anchorRect);
  if (!currentAnchorRect) return;

  items = commands.map((entry, index) => ({
    id: String(entry?.id || ''),
    label: String(entry?.label || entry?.id || ''),
    description: String(entry?.description || ''),
    disabled: !!entry?.disabled,
    index
  }));
  filterQuery = '';
  if (!items.length) return;

  onSelectCb = onSelect;
  onCloseCb = onClose;
  restoreFocusCb = restoreFocus;
  ensureActiveIndex();

  _handle = openPopoverMenu(null, {
    owner: 'iframe-text-edit',
    className: 'pb-slash-menu',
    useBaseClass: false,
    align: 'left',
    gap: 8,
    menuLabel: 'Slash commands',
    getAnchorRect: () => currentAnchorRect,
    consumeCanvasDismiss: false,
    render: ({ close, menu }) => {
      menuEl = menu;
      return buildSlashMenuFragment({ close });
    },
    restoreFocus: () => {
      if (typeof restoreFocusCb === 'function') restoreFocusCb();
    },
    onClose: clearStateAfterClose
  });

  paintActive();
  const focusTarget =
    menuEl?.querySelector('.pb-slash-menu-filter') ||
    menuEl?.querySelector(
      '.pb-slash-menu-item.pb-slash-menu-item-active:not(.pb-slash-menu-item-disabled)'
    ) ||
    menuEl?.querySelector('.pb-slash-menu-item:not(.pb-slash-menu-item-disabled)');
  focusTarget?.focus();
}
